import { useMemo } from 'react'
import type { CounterRules } from '@commerce/core'
import { useStorefrontConfig } from '../config/StorefrontConfigContext'

/**
 * The quantity rules the storefront authored, in the shape `counterMachine`
 * reads.
 *
 * Three screens need these — cart, product detail and anywhere else a stepper
 * appears — and all three had the same literal. A storefront that sells by the
 * case has `step: 12`, and a `+` that moves by 1 sends a quantity its own
 * backend rejects, so this is not a detail worth retyping.
 *
 * `max` falls back to a large number rather than `Infinity`: `counterView`
 * compares against it to decide whether `+` is disabled, and `Infinity` would
 * be correct arithmetic but would also flow into `aria-valuemax`, where a
 * screen reader announces it as "infinity".
 */
export const useCounterRules = (): CounterRules => {
  const { commerce } = useStorefrontConfig()

  return useMemo(
    () => ({
      // Zero is a real quantity here: stepping a cart line to 0 removes it.
      min: 0,
      max: commerce.counter.max ?? 9999,
      step: commerce.counter.step,
    }),
    [commerce],
  )
}
